import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
export const EventLogStatusSchema = z.enum(['OK', 'FAILED', 'WARNING', 'INFO']);
const ListInput = z.object({
    cursor: z
        .object({
        id: z.string(),
        performedAt: z.number(),
        direction: z.enum(['forward', 'backward']).default('forward'),
    })
        .optional(),
    sort: z
        .object({
        field: z.string().default('performedAt'),
        dir: z.enum(['asc', 'desc']).default('desc'),
    })
        .optional(),
    multiFilters: z
        .array(z.discriminatedUnion('id', [
        z.object({
            id: z.literal('performedAt'),
            value: z.array(z.number()).optional(),
            operator: z.enum(['IS EMPTY', 'IS NOT EMPTY', 'IS BETWEEN', '<', '<=', '=', '>', '>=', '!=']),
        }),
        z.object({
            id: z.enum(['status', 'resource', 'domain', 'action', 'performedBy']),
            value: z.array(z.string()).optional(),
            operator: z.enum(['IS EMPTY', 'IS NOT EMPTY', 'IN', 'NOT IN']),
        }),
    ]))
        .optional(),
    filters: z
        .object({
        name: z.string().optional(),
    })
        .optional(),
    limit: z.number().optional(),
});
const ListPatientEventsInput = z.object({
    patientId: z.string(),
    cursor: z
        .object({
        id: z.string(),
        performedAt: z.number(),
        direction: z.enum(['forward', 'backward']).default('forward'),
    })
        .optional(),
    limit: z.number().optional(),
});
/** Logs about a patient: those on the patient itself (resourceId is the patient) and on its protocols, prescriptions and GFRs. */
export function patientEventsQuery(patientId) {
    return zql.eventLogs.where(({ and, cmp, exists, or }) => or(cmp('resourceId', '=', patientId), and(cmp('resource', '=', 'patient-protocol'), exists('patientProtocol', q => q.where('patientId', '=', patientId))), and(cmp('resource', '=', 'patient-therapy-sequence'), exists('patientTherapySequence', q => q.where('patientId', '=', patientId))), and(cmp('resource', '=', 'patient-therapy-sequence-medicine'), exists('patientTherapySequenceMedicine', q => q.where('patientId', '=', patientId))), and(cmp('resource', '=', 'patient-gfr'), exists('patientGfr', q => q.where('patientId', '=', patientId)))));
}
const GetResourceEventsInput = z.object({
    resourceId: z.string(),
    performedBefore: z.number().optional(),
});
export const eventLogsQueries = {
    listEvents: defineQuery(ListInput, ({ args }) => {
        let q = zql.eventLogs.where('id', 'IS NOT', null).related('actor');
        const name = args.filters?.name;
        if (name) {
            q = q.where(({ or, cmp, exists }) => or(cmp('event', 'ILIKE', `%${name}%`), cmp('resource', 'ILIKE', `%${name}%`), cmp('domain', 'ILIKE', `%${name}%`), cmp('search', 'ILIKE', `%${name}%`), exists('actor', q => q.where('name', 'ILIKE', `%${name}%`))));
        }
        if (args.multiFilters) {
            for (const filter of args.multiFilters) {
                // Changed to for...of
                if (filter.id === 'performedAt') {
                    // 1. Handle Null-checks first
                    if (filter.operator === 'IS EMPTY') {
                        q = q.where(filter.id, 'IS', null);
                        continue; // Now valid!
                    }
                    if (filter.operator === 'IS NOT EMPTY') {
                        q = q.where(filter.id, 'IS NOT', null);
                        continue;
                    }
                    // 2. Guard against missing values
                    const timestamps = filter.value;
                    if (!timestamps || timestamps.length === 0)
                        continue;
                    const t1 = getMinuteRange(timestamps[0]);
                    const t2 = timestamps[1] !== undefined ? getMinuteRange(timestamps[1]) : t1;
                    switch (filter.operator) {
                        case 'IS BETWEEN':
                            q = q.where(({ cmp, and }) => and(cmp(filter.id, '>=', t1.start), cmp(filter.id, '<=', t2.end)));
                            break;
                        case '=':
                            q = q.where(({ cmp, and }) => and(cmp(filter.id, '>=', t1.start), cmp(filter.id, '<=', t1.end)));
                            break;
                        case '!=':
                            q = q.where(({ cmp, or }) => or(cmp(filter.id, '<', t1.start), cmp(filter.id, '>', t1.end)));
                            break;
                        case '>':
                            q = q.where(filter.id, '>', t1.end);
                            break;
                        case '<=':
                            q = q.where(filter.id, '<=', t1.end);
                            break;
                        default:
                            q = q.where(filter.id, filter.operator, t1.start);
                            break;
                    }
                }
                else {
                    switch (filter.operator) {
                        case 'IS EMPTY': {
                            q = q.where(({ cmp, or }) => or(cmp(filter.id, 'IS', null), cmp(filter.id, '=', '')));
                            break;
                        }
                        case 'IS NOT EMPTY': {
                            q = q.where(({ cmp, and }) => and(cmp(filter.id, 'IS NOT', null), cmp(filter.id, '!=', '')));
                            break;
                        }
                        default: {
                            if (filter.value && filter.value.length > 0) {
                                q = q.where(filter.id, filter.operator, filter.value);
                            }
                            break;
                        }
                    }
                }
            }
        }
        q = applyCursor(q, { sort: args.sort, cursor: args.cursor, limit: args.limit });
        return q;
    }),
    listPatientEvents: defineQuery(ListPatientEventsInput, ({ args }) => {
        let q = patientEventsQuery(args.patientId).related('actor');
        q = applyCursor(q, { sort: { field: 'performedAt', dir: 'desc' }, cursor: args.cursor, limit: args.limit });
        return q;
    }),
    getEventById: defineQuery(z.string(), ({ args }) => {
        let base = zql.eventLogs.where('id', '=', args).related('actor');
        return base.one();
    }),
    getResourceEvents: defineQuery(GetResourceEventsInput, ({ args }) => {
        let base = zql.eventLogs.where('resourceId', args.resourceId);
        if (args.performedBefore) {
            base = base.where('performedAt', '<=', args.performedBefore);
        }
        base = base.related('actor').orderBy('performedAt', 'asc');
        return base;
    }),
};
function getMinuteRange(timestamp) {
    return {
        start: timestamp * 1000,
        end: timestamp * 1000 + 59999,
    };
}
//# sourceMappingURL=event-log.queries.js.map